<template>
    <div v-if="!isLoading">
        <data-table
            v-if="collection.columns.length > 0"
            :auth="auth"
            :collection="collection"
            :is-loading="isLoading"
            :row-set="hasDefaultRows.includes('main') ? null : rows"
            with-scroll-sync
        >
            <template #title>
                <div class="flex justify-between items-center">
                    <span>{{ $t('general.overall') }}</span>

                    <default-button
                        :color="hasDefaultRows.includes('main') ? 'green' : 'indigo'"
                        :label="$t('storemanager.show-all-rows')"
                        class="ml-2"
                        icon="color-swatch"
                        size="xxs"
                        @click.native="setDefaultRows('main', !hasDefaultRows.includes('main'))"
                    ></default-button>
                </div>
            </template>
        </data-table>

        <data-table
            v-for="data in storeData"
            :key="data.store.id"
            :auth="auth"
            :collection="data.columns"
            :is-loading="isLoading"
            :row-set="hasDefaultRows.includes(data.store.id) ? null : rows"
            with-scroll-sync
        >
            <template #title>
                <div class="flex justify-between items-center">
                    <a
                        :href="route('stores.profit-and-loss.edit', [data.store.id, currentDate.year(), currentDate.month() + 1])"
                        class="cursor-pointer w-full h-full hover:font-bold"
                        target="_blank"
                    >
                        #{{ data.store.number }}
                    </a>

                    <default-button
                        :color="hasDefaultRows.includes(data.store.id) ? 'green' : 'indigo'"
                        :label="$t('storemanager.show-all-rows')"
                        class="ml-2"
                        icon="color-swatch"
                        size="xxs"
                        @click.native="setDefaultRows(data.store.id, !hasDefaultRows.includes(data.store.id))"
                    ></default-button>
                </div>
            </template>
        </data-table>
    </div>
</template>

<script>
import DataTable from '@/Components/Storemanager/DataTable';
import DataColumnCollection from '@/Components/Storemanager/Entities/DataColumnCollection';
import BudgetEntry from '@/Models/BudgetEntry';
import Projection from '@/Models/Projection';
import ProfitAndLoss from '@/Models/ProfitAndLoss';
import Actual from '@/Models/Actual';
import DataColumn from '@/Components/Storemanager/Entities/DataColumn';
import Helper from '@/Utilities/Helper';
import Extrapolation from '@/Models/Extrapolation';

export default {
    components: {DataTable},

    props: {
        evaluationData: Object,
        date: Date,
    },

    data() {
        return {
            isLoading: true,
            hasDefaultRows: [],
            rows: [
                storeManager.getFieldByNumber(1),
                storeManager.getFieldByNumber(52),
                storeManager.getFieldByNumber(60),
                storeManager.getFieldByNumber(98),
                storeManager.getFieldByNumber(105),
                storeManager.getFieldByNumber(600),
            ],

            collection: new DataColumnCollection([], true),
            storeData: [],
        };
    },

    created() {
        this.prepareColumns(this.evaluationData);
        this.isLoading = false;
    },

    computed: {
        currentDate() {
            return moment.utc(this.date);
        },
    },

    methods: {
        setDefaultRows(key, shouldHaveDefault) {
            if (shouldHaveDefault) {
                if (this.hasDefaultRows.includes(key)) {
                    return;
                }

                this.hasDefaultRows.push(key);
            } else {
                if (!this.hasDefaultRows.includes(key)) {
                    return;
                }

                this.hasDefaultRows.splice(this.hasDefaultRows.findIndex(value => value === key), 1);
            }
        },
        prepareColumns(data) {
            let columns = [];

            let date = moment.utc(this.currentDate);
            let results = data.results;

            // Default Entities
            let budgetEntry = new BudgetEntry(results.budgetEntry ?? {date: moment.utc(date)}, false);
            let projection = new Projection(results.projection ?? {date: moment.utc(date)}, false);
            let profitAndLoss = new ProfitAndLoss(results.profitAndLoss ?? {date: moment.utc(date)}, false);
            let actual = new Actual(results.actual ?? {date: moment.utc(date)}, false);

            columns.push(new DataColumn(budgetEntry));
            columns.push(new DataColumn(projection));

            let dates = Helper.getExtrapolationDatesForMonth(date.year(), date.month() + 1);

            dates.forEach(date => {
                let extrapolation = new Extrapolation({date: date});

                if (Array.isArray(results.extrapolations) && results.extrapolations.length > 0) {
                    let foundExtrapolation = results.extrapolations.find(
                        extrapolation => date.isSame(extrapolation.date));

                    if (foundExtrapolation) {
                        extrapolation = new Extrapolation(foundExtrapolation);
                    }
                }

                columns.push(new DataColumn(extrapolation));
            });

            columns.push(new DataColumn(profitAndLoss));
            columns.push(new DataColumn(actual));

            this.collection.columns = columns;
            // Overall collection done

            if (data.hasOwnProperty('storeData') && data.storeData && data.storeData.length > 0) {
                data.storeData.forEach(entry => {
                    let columns = [];
                    let budgetEntry = new BudgetEntry(entry.budgetEntry ?? {date: moment.utc(date)});
                    let projection = new Projection(entry.projection ?? {date: moment.utc(date)});
                    let profitAndLoss = new ProfitAndLoss(entry.profitAndLoss ?? {date: moment.utc(date)});
                    let actual = new Actual(entry.actual ?? {date: moment.utc(date)});

                    columns.push(new DataColumn(budgetEntry));
                    columns.push(new DataColumn(projection));

                    let dates = Helper.getExtrapolationDatesForMonth(date.year(), date.month() + 1);

                    dates.forEach(date => {
                        let extrapolation = new Extrapolation({date: date});

                        if (Array.isArray(entry.extrapolations) && entry.extrapolations.length > 0) {
                            let foundExtrapolation = entry.extrapolations.find(
                                extrapolation => date.isSame(extrapolation.date));

                            if (foundExtrapolation) {
                                extrapolation = new Extrapolation(foundExtrapolation);
                            }
                        }

                        columns.push(new DataColumn(extrapolation));
                    });

                    columns.push(new DataColumn(profitAndLoss));
                    columns.push(new DataColumn(actual));

                    this.storeData.push({
                        store: entry.store,
                        columns: new DataColumnCollection(columns, true),
                    });
                });
            }
        },
    },
};
</script>
